import { useEffect, useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import type { MapleMarketApp, MapleMarketCategory } from '@/data/mapleMarketApps';

interface BasicTabProps {
  product: MapleMarketApp;
  disabled: boolean;
  onSaved: () => Promise<void> | void;
}

const categories: MapleMarketCategory[] = ['Games', 'Office', 'Creative', 'Developer', 'Music', 'Utilities'];

export default function BasicTab({ product, disabled, onSaved }: BasicTabProps) {
  const { toast } = useToast();
  const [name, setName] = useState(product.name);
  const [developer, setDeveloper] = useState(product.developer);
  const [category, setCategory] = useState<MapleMarketCategory>(product.category);
  const [tags, setTags] = useState(product.tags.join(', '));
  const [price, setPrice] = useState(String(product.priceCents / 100));
  const [isFree, setIsFree] = useState(product.isFree);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setName(product.name);
    setDeveloper(product.developer);
    setCategory(product.category);
    setTags(product.tags.join(', '));
    setPrice(String(product.priceCents / 100));
    setIsFree(product.isFree);
  }, [product]);

  useEffect(() => {
    if (isFree) {
      setPrice('0');
    }
  }, [isFree]);

  const handleSave = async () => {
    const priceValue = isFree ? 0 : Math.max(0, Math.round(Number(price || '0') * 100));
    if (!name.trim() || !developer.trim()) {
      toast({
        title: 'Missing details',
        description: 'Name and developer are required.',
        variant: 'destructive',
      });
      return;
    }

    if (!isFree && Number.isNaN(priceValue)) {
      toast({
        title: 'Invalid price',
        description: 'Enter a valid price before saving.',
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);

    const { error } = await (supabase.from('marketplace_products') as any)
      .update({
        name: name.trim(),
        developer: developer.trim(),
        category,
        tags: tags
          .split(',')
          .map((tag) => tag.trim())
          .filter(Boolean),
        price_cents: priceValue,
      })
      .eq('id', product.id);

    setSaving(false);

    if (error) {
      toast({
        title: 'Save failed',
        description: error.message,
        variant: 'destructive',
      });
      return;
    }

    await onSaved();
    toast({
      title: 'Product updated',
      description: `${name} was saved successfully.`,
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Basic information</h3>
          <p className="text-sm text-muted-foreground">Update the core storefront metadata for this application.</p>
        </div>
        <Button type="button" onClick={() => void handleSave()} disabled={disabled || saving}>
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
          Save changes
        </Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="market-product-name">Product name</Label>
          <Input id="market-product-name" value={name} onChange={(event) => setName(event.target.value)} disabled={disabled} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="market-product-developer">Developer</Label>
          <Input
            id="market-product-developer"
            value={developer}
            onChange={(event) => setDeveloper(event.target.value)}
            disabled={disabled}
          />
        </div>
        <div className="space-y-2">
          <Label>Category</Label>
          <Select value={category} onValueChange={(value) => setCategory(value as MapleMarketCategory)} disabled={disabled}>
            <SelectTrigger>
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              {categories.map((categoryOption) => (
                <SelectItem key={categoryOption} value={categoryOption}>
                  {categoryOption}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="market-product-tags">Tags</Label>
          <Input
            id="market-product-tags"
            value={tags}
            onChange={(event) => setTags(event.target.value)}
            disabled={disabled}
            placeholder="RPG, Multiplayer, Productivity"
          />
          <p className="text-xs text-muted-foreground">Separate tags with commas.</p>
        </div>
      </div>

      <div className="grid gap-6 rounded-xl border border-border p-4 lg:grid-cols-[1fr_auto] lg:items-end">
        <div className="space-y-2">
          <Label htmlFor="market-product-price">Price (USD)</Label>
          <Input
            id="market-product-price"
            type="number"
            min="0"
            step="0.01"
            value={price}
            onChange={(event) => setPrice(event.target.value)}
            disabled={disabled || isFree}
          />
        </div>
        <div className="flex items-center justify-between gap-4 rounded-lg bg-muted/40 px-4 py-3">
          <div>
            <div className="font-medium text-foreground">Free product</div>
            <div className="text-sm text-muted-foreground">Turn this on to set price to $0.00.</div>
          </div>
          <Switch checked={isFree} onCheckedChange={setIsFree} disabled={disabled} />
        </div>
      </div>
    </div>
  );
}